/**
 * Data layer for `advanced-retargeting-controls-for-smarter-compliance`.
 * Typed fetch wrappers over /api/v1/advanced-retargeting-controls-for-smarter-compliance.
 * Raw `fetch()` is confined to this file per the dash contract (D6).
 */

export interface RetargetingRule {
	id: string;
	rule_key: string;
	name: string;
	jurisdiction: string;
	consent_required: boolean;
	pii_excluded: boolean;
	excluded_audience_ids: string[];
	frequency_cap: number;
	enabled: boolean;
	created_at: number;
}

export interface ListRetargetingRulesResponse {
	items: RetargetingRule[];
}

export interface CreateRetargetingRuleBody {
	rule_key: string;
	name: string;
	jurisdiction: string;
	consent_required?: boolean;
	pii_excluded?: boolean;
	excluded_audience_ids?: string[];
	frequency_cap?: number;
	enabled?: boolean;
}

export interface ListFilters {
	jurisdiction?: string;
	enabled?: "0" | "1";
}

const BASE = "/api/v1/advanced-retargeting-controls-for-smarter-compliance";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

function buildQuery(filters?: ListFilters): string {
	if (!filters) return "";
	const qs = new URLSearchParams();
	if (filters.jurisdiction) qs.set("jurisdiction", filters.jurisdiction);
	if (filters.enabled === "0" || filters.enabled === "1") {
		qs.set("enabled", filters.enabled);
	}
	const s = qs.toString();
	return s ? `?${s}` : "";
}

export async function list(
	filters?: ListFilters,
): Promise<ListRetargetingRulesResponse> {
	const res = await fetch(`${BASE}${buildQuery(filters)}`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListRetargetingRulesResponse>(res);
}

export async function create(
	body: CreateRetargetingRuleBody,
): Promise<{ ok: true; id: string }> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<{ ok: true; id: string }>(res);
}

export async function remove(id: string): Promise<{ ok: true }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ ok: true }>(res);
}

export const advancedRetargetingControlsForSmarterComplianceQueryKeys = {
	all: ["advanced-retargeting-controls-for-smarter-compliance"] as const,
	list: (filters?: ListFilters) =>
		[
			...advancedRetargetingControlsForSmarterComplianceQueryKeys.all,
			filters ?? {},
		] as const,
	detail: (id: string) =>
		[
			...advancedRetargetingControlsForSmarterComplianceQueryKeys.all,
			id,
		] as const,
};
